import {DataTable, DataTableProps} from "../DataTable/DataTable";
import {SiteSiteAssocEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {Anchor} from "@mantine/core";
import {Link} from "react-router-dom";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {Badge} from "../../badge/Badge/Badge";
import {useEffect, useMemo, useState} from "react";
import type {DataTableColumn} from "mantine-datatable/dist/types/DataTableColumn";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {DataTableSortStatus} from "mantine-datatable";
import {sortBy} from "lodash";
import {useSiteSiteAssocDataTableFilter} from "./useSiteSiteAssocDataTableFilter";
import {usePagination} from "../DataTable/usePagination";
import { DefaultText } from "../../text/DefaultText/DefaultText";
import { FormattedQuantity } from "../../text/FormattedQuantity/FormattedQuantity";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";

export type SiteSiteAssocDataTableProps =
 EntityDataTableProps<SiteSiteAssocEntity>
  & {
  showSource?: boolean
  showDest?: boolean
}

const EMPTY_ARRAY: SiteSiteAssocEntity[] = []

export const SiteSiteAssocDataTable = (props: SiteSiteAssocDataTableProps) => {
  const {
    records: assocs = EMPTY_ARRAY,
    recordsPerPageOptions,
    showSource = false,
    showDest = true,
    ...rest
  } = props;

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useSiteSiteAssocDataTableFilter({
    records: assocs
  })

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus>({
    columnAccessor: 'sourceSiteName',
    direction: 'asc'
  });
  const [sortedRecords, setSortedRecords] = useState(sortBy(filteredRecords, 'sourceSiteName'));

  useEffect(() => {
    const data = sortBy(filteredRecords, sortStatus.columnAccessor) as SiteSiteAssocEntity[];
    setSortedRecords(sortStatus.direction === 'desc' ? data.reverse() : data);
  }, [filteredRecords, sortStatus]);

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const visibleColumns: DataTableColumn<SiteSiteAssocEntity>[] = useMemo(() => {
    const columns: DataTableColumn<SiteSiteAssocEntity>[] = []

    if (showSource) {
      columns.push({
        ...getColumnFilterProps(filterConfig, "sourceName"),
        title: t('common.entity.sitesiteassoc.table.column.source', "Source"),
        accessor: "sourceSiteName",
        sortable: true,
        render: ({sourceSiteId, sourceSiteName}) => {
          return (
            <Anchor component={Link} to={`/sites/profile?id=${sourceSiteId}`}>
              <DefaultText value={sourceSiteName}/>
            </Anchor>
          )
        }
      })
    }

    if (showDest) {
      columns.push({
        ...getColumnFilterProps(filterConfig, "destName"),
        title: t('common.entity.sitesiteassoc.table.column.dest', "Destintation"),
        accessor: "sourceSiteName",
        sortable: true,
        render: ({destSiteId, destSiteName}) => {
          return (
            <Anchor component={Link} to={`/sites/profile?id=${destSiteId}`}>
              <DefaultText value={destSiteName}/>
            </Anchor>
          )
        }
      })
    }

    columns.push({
      title: t('common.entity.sitesiteassoc.table.column.mileage', "Mileage"),
      accessor: "mileage",
      sortable: true,
      render: ({mileage}) => {
        if (typeof mileage !== 'undefined') {
          return <FormattedQuantity value={{number: mileage}}/>
        }

        return undefined
      }
    })

    columns.push({
      title: t('common.entity.sitesiteassoc.table.column.status', "Status"),
      accessor: "active",
      render: (assoc) => {
        if (assoc.isActive()) {
          return <Badge color="green">{t("common.status.active", "Active")}</Badge>
        }

        return undefined
      }
    })

    columns.push({
      title: t('common.entity.sitesiteassoc.table.column.effective', "Effective"),
      accessor: "effectiveDttm",
      sortable: true,
      render: ({effectiveDttm}) => (effectiveDttm ? <FormattedDateTime date={effectiveDttm}/> : undefined)
    })

    columns.push({
      title: t('common.entity.sitesiteassoc.table.column.end', "End"),
      accessor: "endDttm",
      sortable: true,
      render: ({endDttm}) => (endDttm ? <FormattedDateTime date={endDttm}/> : undefined)
    })

    return columns
  }, [t, filterConfig, showSource, showDest])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={setSortStatus}
      columns={visibleColumns}
    />
  );
};
